/* What the Optimizer's review screen shows, and every sentence on it.
 *
 * Between a stored block and the desk. Which days appear, what a block gains as lines
 * are ticked, what Approve says and sends, and what became of each line. The page is
 * js/components/optimizer.js, the data is js/optimizer.js, and what a block IS is the
 * header of js/rules/optimizer.js.
 *
 * Dismissals are filtered here as well as in the check. One can land between two
 * checks, from another station, and the screen must not go on offering it.
 *
 * Times on a block are milliseconds of the salon's wall clock, labelled UTC, so they
 * are read back with the UTC getters and never through the browser's own zone.
 *
 * Pure: arguments in, plain objects out.
 */
import { dismissalKeys, gainFor, highlightText } from './optimizer.js';
import { clockOf, shortClient } from './pending-moves.js';

// The same default as the optimizer function's.
var DEFAULT_TZ = 'America/Chicago';

var WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

var MOVES_OFF = 'Moves are off: this salon has no Mangomint write key.';
var UNSURE = 'That may have gone through. The next check will show it.';
var JUST_CHECKED = 'Checked a moment ago. Try again in a couple of minutes.';

var ERRORS = {
  no_write_key: MOVES_OFF,
  no_proxy_url: 'Mangomint isn\'t connected for this salon.',
  optimizer_off: 'The Optimizer is switched off for this salon.',
  role_not_allowed: 'This sign-in can\'t use the Optimizer.',
  not_licensed: 'The Optimizer isn\'t on this salon\'s plan.',
  offline: 'This station is offline, so nothing was sent.',
  timeout: UNSURE
};

/* ---- Days ---- */

function dateLabel(date) {
  var p = String(date).split('-').map(Number);
  var d = new Date(Date.UTC(p[0], p[1] - 1, p[2]));
  return WEEKDAYS[d.getUTCDay()] + ' ' + MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
}

function compare(a, b) { return a < b ? -1 : (a > b ? 1 : 0); }

/* The block as the desk should see it, or null when nothing on it is left to decide. */
function visibleBlock(block, dismissed) {
  var lines = (block.lines || []).filter(function (l) {
    return l.kind !== 'move' || !dismissed[dismissalKeys(l)[0]];
  });
  if (!lines.some(function (l) { return l.kind === 'move'; })) return null;
  return Object.assign({}, block, { lines: lines });
}

/* [{ date, label, dateLabel, expanded, count, countLabel, highlight, blocks }], only for
 * days from `today` on with something to show. `highlight` is the sentence, empty when
 * there is none.
 *
 *   opts  { days: tt_optimizer_days rows, dismissals: tt_optimizer_dismissals rows } */
export function dayGroups(blocks, today, opts) {
  var o = opts || {};
  var byDate = {};
  function dayOf(date) {
    if (!byDate[date]) byDate[date] = { date: date, blocks: [], raw: [] };
    return byDate[date];
  }
  var dismissedOn = {};
  (o.dismissals || []).forEach(function (d) {
    (dismissedOn[d.date] || (dismissedOn[d.date] = {}))[d.booking_key] = true;
  });
  (blocks || []).forEach(function (block) {
    if (!block.date || block.date < today) return;
    var shown = visibleBlock(block, dismissedOn[block.date] || {});
    if (shown) dayOf(block.date).blocks.push(shown);
  });
  (o.days || []).forEach(function (d) {
    if (!d.date || d.date < today || !Array.isArray(d.highlight) || !d.highlight.length) return;
    dayOf(d.date).raw = d.highlight;
  });
  return Object.keys(byDate).sort().map(function (date) {
    var day = byDate[date];
    var isToday = date === today;
    var shown = day.blocks.slice().sort(function (a, b) {
      return compare(a.staffName, b.staffName) ||
        compare(a.priority && a.priority.label, b.priority && b.priority.label) || compare(a.key, b.key);
    });
    return {
      date: date,
      label: isToday ? 'Today' : dateLabel(date),
      dateLabel: dateLabel(date),
      expanded: isToday,
      count: shown.length,
      countLabel: shown.length === 1 ? '1 block' : shown.length + ' blocks',
      highlight: highlightText(day.raw, isToday),
      blocks: shown
    };
  });
}

/* The quiet number on the switcher. */
export function pendingCount(blocks, today, opts) {
  return dayGroups(blocks, today, opts).reduce(function (n, d) { return n + d.count; }, 0);
}

/* ---- One block ---- */

export function blockTitle(block) {
  return 'Free up ' + block.staffName + ' for ' + ((block.priority && block.priority.label) || 'this');
}

function countOf(ticked) {
  return Array.isArray(ticked) ? ticked.length : (Number(ticked) || 0);
}

export function approveLabel(_block, ticked) {
  var n = countOf(ticked);
  return 'Approve ' + n + (n === 1 ? ' move' : ' moves');
}

/* { label, disabled, reason }. `reason` is for what can't be done at all, not for
 * nothing being ticked, which the box beside each line already says. */
export function approveState(block, ticked, ctx) {
  var off = !!block.movesOff;
  return {
    label: approveLabel(block, ticked),
    disabled: off || countOf(ticked) === 0 || !!(ctx && ctx.busy),
    reason: off ? MOVES_OFF : null
  };
}

function isSettled(result) {
  return !!result && (result.outcome === 'moved' || result.outcome === 'already');
}

/* May the desk tick this line? Only a move, on a block with moves on, not already made. */
export function canTick(line, block, results) {
  return line.kind === 'move' && !block.movesOff && !isSettled(results && results[line.id]);
}

/* How a block's boxes start. The check decides, and a warned or unread line is held to
 * unticked here too, because this is the last word before Approve. */
export function initialTicks(block) {
  var out = {};
  (block.lines || []).forEach(function (l) {
    if (l.kind === 'move' && l.ticked === true && !(l.warn && l.warn.length) && !l.notesUnread) out[l.id] = true;
  });
  return out;
}

/* What the desk did by hand, kept by date and line and not by block. A block's key changes
 * when any booking on it does, and a line somebody deliberately unticked must not come
 * back ticked for that.
 *
 *   hand  { handKey: { on, warned } }, `warned` being what the line said when it was set */
export function handKey(date, lineId) { return date + '|' + lineId; }

function isWarned(line) { return !!(line.warn && line.warn.length) || !!line.notesUnread; }

export function handEntry(line, on) { return { on: !!on, warned: isWarned(line) }; }

/* A block's boxes, with the hand's over the check's. A hand tick given before a warning
 * appeared wasn't given knowing about it, so that one goes back to unticked. */
export function ticksFor(block, hand) {
  var out = initialTicks(block);
  (block.lines || []).forEach(function (l) {
    var entry = hand && hand[handKey(block.date, l.id)];
    if (!entry || l.kind !== 'move') return;
    if (entry.on && !entry.warned && isWarned(l)) return;
    out[l.id] = entry.on;
  });
  return out;
}

/* The hand's entries for lines still on some block. */
export function pruneHand(hand, blocks) {
  var live = {};
  (blocks || []).forEach(function (b) {
    (b.lines || []).forEach(function (l) { live[handKey(b.date, l.id)] = true; });
  });
  var out = {};
  Object.keys(hand || {}).forEach(function (k) { if (live[k]) out[k] = hand[k]; });
  return out;
}

/* The salon's date, from its own zone, which is what the check dates its rows by. A station
 * with its clock in another zone still agrees. */
export function salonToday(now, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || DEFAULT_TZ, year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(now);
  } catch (e) {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: DEFAULT_TZ, year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(now);
  }
}

/* The ids Approve would send. */
export function tickedIds(block, ticks, results) {
  return (block.lines || []).filter(function (l) {
    return canTick(l, block, results) && ticks[l.id] === true;
  }).map(function (l) { return l.id; });
}

/* The ids already made, which count towards what the block has gained. */
export function settledIds(block, results) {
  return (block.lines || []).filter(function (l) {
    return l.kind === 'move' && isSettled(results && results[l.id]);
  }).map(function (l) { return l.id; });
}

function clockFromWall(ms) {
  var d = new Date(ms);
  var h = d.getUTCHours();
  var m = d.getUTCMinutes();
  return (h % 12 || 12) + ':' + (m < 10 ? '0' : '') + m + (h < 12 ? ' AM' : ' PM');
}

function lengthOf(minutes) {
  var h = Math.floor(minutes / 60);
  var m = minutes % 60;
  if (h && m) return h + 'h ' + m + 'm';
  return h ? h + 'h' : m + 'm';
}

// "Facials" reads "earliest facial". "Dip Powder" has nothing to take off, and neither
// does a word ending in a double s.
function serviceWord(block) {
  var word = String((block.priority && block.priority.label) || 'slot').toLowerCase();
  return /[^s]s$/.test(word) ? word.slice(0, -1) : word;
}

/* "Opens 4h 15m, earliest facial 2:00 PM → 12:00 PM". The minutes appear only when the
 * earliest slot moves. Given `tickedIds` it is worked out for those lines, and without
 * them it is what the whole block gains. */
export function gainLine(block, ticked) {
  var gain = ticked ? gainFor(block, ticked) : (block.gain || gainFor(block, (block.basis.moves || []).map(function (m) { return m.id; })));
  var word = serviceWord(block);
  if (gain.earliestTo == null) return 'No opening for a ' + word + ' yet';
  if (gain.earliestFrom === gain.earliestTo) {
    return 'Earliest ' + word + ' stays ' + clockFromWall(gain.earliestTo);
  }
  var from = gain.earliestFrom == null ? 'no opening' : clockFromWall(gain.earliestFrom);
  return 'Opens ' + lengthOf(gain.minutes) + ', earliest ' + word + ' ' + from + ' → ' + clockFromWall(gain.earliestTo);
}

/* ---- One line ---- */

export function lineText(line) {
  return {
    time: clockOf(line.starts_at),
    service: line.service || 'A service',
    client: shortClient(line.clientName),
    from: line.staffName,
    to: line.toName
  };
}

/* What goes under a line, each { kind: 'warn' | 'note' | 'why' | 'stay', text }. */
export function lineNotes(line) {
  if (line.kind !== 'move') return line.why ? [{ kind: 'stay', text: line.why }] : [];
  var out = [];
  if (line.notes) {
    var warned = line.warn && line.warn.length;
    out.push(warned
      ? { kind: 'warn', text: 'Note names ' + line.warn.join(', ') + ': “' + line.notes + '”' }
      : { kind: 'note', text: 'Note: “' + line.notes + '”' });
  }
  if (line.why) out.push({ kind: line.notesUnread ? 'warn' : 'why', text: line.why });
  return out;
}

/* { kind: 'ok' | 'dry' | 'bad' | 'wait', text }, or null for a line not sent. */
export function resultText(result) {
  if (!result) return null;
  switch (result.outcome) {
    case 'moved':
    case 'already':
      if (result.overlaps && result.overlaps.length) {
        return { kind: 'bad', text: 'Moved, but Mangomint shows an overlap. Fix it in Mangomint.' };
      }
      return { kind: 'ok', text: result.outcome === 'moved' ? 'Moved.' : 'Already moved.' };
    case 'dry_run':
      return { kind: 'dry', text: 'Dry run: nothing saved.' };
    case 'refused':
      return { kind: 'bad', text: result.why || 'This one was refused.' };
    case 'waiting':
      return { kind: 'wait', text: result.why || 'Waiting on Mangomint. Try again shortly.' };
    case 'unknown':
      return { kind: 'wait', text: UNSURE };
    default:
      return { kind: 'wait', text: 'Not sent.' };
  }
}

/* ---- Approve ---- */

function wireOf(line) {
  return {
    appointment_id: line.appointment_id,
    starts_at: line.starts_at,
    service_id: line.service_id,
    from_staff_id: line.from_staff_id,
    to_staff_id: line.to_staff_id
  };
}

/* The calls Approve makes: ticked lines in time order, one line to a call. So a refusal
 * ends only that line's run on the server, and the safe moves after it still go through.
 * The Optimizer's moves can do that because they land only in open time and none relies
 * on another. Swap's can't, which is why applyMoves itself stops at a refusal. */
export function approveBatches(block, ids) {
  var lines = (block.lines || []).filter(function (l) {
    return l.kind === 'move' && ids.indexOf(l.id) !== -1;
  }).sort(function (a, b) {
    return (a.fromMs - b.fromMs) || compare(a.starts_at, b.starts_at) || compare(a.id, b.id);
  });
  return lines.map(function (l) { return [wireOf(l)]; });
}

function moveId(m) { return m.appointment_id + ':' + m.service_id + ':' + m.starts_at; }

/* Runs the calls in order. A refused line stays with its reason and the rest carry on.
 * It stops at `waiting`, because the next call would only get the same answer, and at a
 * call with no lines, which says something about the salon or the connection.
 *
 *   call(moves) resolves to the function's answer. Returns { results: { id: line },
 *   message, dryRun }. A call with an unknown ending marks its lines unknown, not
 *   failed: they may have gone through. */
export async function runBatches(batches, call) {
  var results = {};
  var message = null;
  var dryRun = false;
  var stopped = false;

  function mark(moves, outcome) {
    moves.forEach(function (m) { results[moveId(m)] = { id: moveId(m), outcome: outcome, why: null, overlaps: [] }; });
  }

  for (var i = 0; i < batches.length; i++) {
    var moves = batches[i];
    if (stopped) { mark(moves, 'not_tried'); continue; }
    var answer;
    try {
      answer = await call(moves);
    } catch (e) {
      answer = { error: 'timeout' };
    }
    if (answer && answer.results && answer.results.length) {
      dryRun = dryRun || !!answer.dry_run;
      answer.results.forEach(function (r) { results[r.id] = r; });
      stopped = answer.results.some(function (r) { return r.outcome === 'waiting'; });
      continue;
    }
    var code = (answer && answer.error) || 'timeout';
    message = errorMessage(code, answer && answer.message);
    mark(moves, code === 'timeout' ? 'unknown' : 'not_tried');
    stopped = true;
  }
  return { results: results, message: message, dryRun: dryRun };
}

export function errorMessage(code, message) {
  if (code === 'refused') return ERRORS[message] || message || 'That was refused.';
  return ERRORS[code] || message || code || 'That did not work.';
}

/* ---- Check now ---- */

/* What to say after the button, or null for nothing: new blocks say it themselves. */
export function checkNowMessage(answer) {
  if (!answer) return 'That check did not finish.';
  if (answer.busy || answer.error === 'just_checked') return JUST_CHECKED;
  if (answer.error === 'timeout') return 'That check is taking a while. What it finds will appear here.';
  if (answer.error) return errorMessage(answer.error, answer.message);
  return answer.ok === false ? 'That check did not finish.' : null;
}

export function checkAge(lastOkAt, nowMs) {
  if (!lastOkAt) return 'Not checked yet';
  var seconds = Math.max(0, (nowMs - Date.parse(lastOkAt)) / 1000);
  if (seconds < 60) return 'Checked just now';
  var minutes = Math.floor(seconds / 60);
  if (minutes < 60) return 'Checked ' + minutes + ' min ago';
  var hours = Math.floor(minutes / 60);
  if (hours < 24) return 'Checked ' + hours + 'h ago';
  var days = Math.floor(hours / 24);
  return 'Checked ' + days + (days === 1 ? ' day ago' : ' days ago');
}
